<template>
  <div
    v-loading="!isDataLoaded"
    class="tw-flex tw-flex-col tw-h-full"
  >
    <div class="tw-flex tw-justify-between tw-items-center tw-mb-4">
      <router-link
        v-if="$isInRole(['FOREIGNER'])"
        :to="'/'"
      >
        <i-button-icon-text
          size="medium"
          icon="icon-angle-left"
        >
          {{ $t('foreigner.permits.back_to_dashboard') }}
        </i-button-icon-text>
      </router-link>
      <h1 class="tw-m-0">
        {{ $t('main.me.title') }}
      </h1>
      <i-button
        v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
        size="medium"
        @click="isOutOfOfficeDialogOpen = true"
      >
        <icon-out-of-office />
        {{ $t('profile.out_of_office.title') }}
      </i-button>
    </div>
    <div
      v-if="$isInRole(['ADMIN', 'CONSULTANT']) && isOutOfOffice"
      class="tw-border tw-border-gray dark:tw-border-dm-gray tw-rounded-3xl tw-py-4 tw-px-8 tw-flex tw-justify-between tw-items-center tw-mb-6"
    >
      <h3 v-html="$t('profile.out_of_office.message', { startDate: me.profile.start_date, startTime: me.profile.start_time, endDate: me.profile.end_date, endTime: me.profile.end_time })" />
      <div class="tw-flex tw-gap-2.5">
        <i-button
          size="medium"
          @click="isOutOfOfficeDialogOpen = true"
        >
          <icon-edit />
          {{ $t('common.actions.edit') }}
        </i-button>
        <i-button
          size="medium"
          type="primary"
          @click="setIsAvailableNow"
        >
          <icon-check />
          {{ $t('profile.out_of_office.available_again') }}
        </i-button>
      </div>
    </div>
    <div class="tw-h-full tw-flex tw-flex-col tw-justify-between">
      <el-form
        v-if="isDataLoaded"
        ref="Form"
        :model="form"
        :rules="formRules"
        label-position="top"
        :disabled="isLoading"
        class="tw-flex tw-gap-10"
        @submit.native.prevent="submitForm"
      >
        <div class="tw-flex tw-flex-col tw-gap-6 tw-flex-1">
          <div class="tw-flex tw-flex-col tw-gap-3">
            <h3 class="tw-leading-6">
              {{ $t('main.me.personal_title') }}
            </h3>
            <div class="tw-flex tw-flex-col tw-gap-2">
              <div class="tw-flex tw-gap-2">
                <div>
                  <i-user-avatar
                    :avatar="userAvatar && userAvatar['128'] && `${$baseHost}/${userAvatar['128']}`"
                    :fullname="me.fullname"
                    size="small"
                    class="tw-w-14 tw-h-14"
                    is-editable
                    @click="$refs.avatarFile.click()"
                  />
                  <input
                    ref="avatarFile"
                    accept="image/*"
                    class="tw-hidden"
                    type="file"
                    @change="uploadAvatar($event.target.files[0])"
                  >
                </div>
                <i-form-item
                  :label="$t('common.fields.first_name')"
                  :error="errors?.first_name?.[0]"
                  :class="form.first_name ? 'has-value' : ''"
                  prop="first_name"
                  class="tw-flex-2"
                >
                  <el-input
                    v-model="form.first_name"
                  />
                </i-form-item>
                <i-form-item
                  :label="$t('common.fields.last_name')"
                  :error="errors?.last_name?.[0]"
                  :class="form.last_name ? 'has-value' : ''"
                  prop="last_name"
                  class="tw-flex-3"
                >
                  <el-input
                    v-model="form.last_name"
                  />
                </i-form-item>
              </div>
              <i-form-item
                :label="$t('common.fields.primary_phone')"
                :error="errors?.phone_primary?.[0]"
                :class="form.phone_primary ? 'has-value' : ''"
                prop="phone_primary"
              >
                <el-input
                  v-model="form.phone_primary"
                />
              </i-form-item>
              <i-form-item
                :label="$t('common.fields.secondary_phone')"
                :error="errors?.phone_secondary?.[0]"
                :class="form.phone_secondary ? 'has-value' : ''"
              >
                <el-input
                  v-model="form.phone_secondary"
                />
              </i-form-item>
              <i-form-item
                :label="$t('common.fields.email')"
                :error="errors?.email?.[0]"
                :class="form.email ? 'has-value' : ''"
              >
                <el-input
                  v-model="form.email"
                />
              </i-form-item>
              <i-form-item
                :label="$t('main.me.interface_language')"
                :error="errors?.interface_language?.[0]"
                :class="form.interface_language ? 'has-value' : ''"
                prop="interface_language_name"
              >
                <el-select
                  v-model="form.interface_language"
                  placeholder=""
                  filterable
                >
                  <el-option
                    v-for="item in interfaceLanguages"
                    :key="item.id"
                    :label="item.name"
                    :value="item.id"
                  />
                </el-select>
              </i-form-item>
            </div>
          </div>
          <div class="tw-flex tw-flex-col tw-gap-4">
            <h3 class="tw-leading-6">
              {{ $t('main.me.password_title') }}
            </h3>
            <div class="tw-flex tw-flex-col tw-gap-3">
              <i-form-item
                :label="$t('common.fields.password_current')"
                :error="errors?.password_current?.[0]"
                :class="form.password_current ? 'has-value' : ''"
                prop="password_current"
                class="tw-flex-2"
              >
                <PasswordInput
                  v-model="form.password_current"
                  disable-autofill
                />
              </i-form-item>
              <i-form-item
                :label="$t('common.fields.new_password')"
                :error="errors?.password?.[0]"
                :class="form.password ? 'has-value' : ''"
                prop="password"
                class="tw-flex-3"
              >
                <PasswordInput
                  v-model="form.password"
                  disable-autofill
                />
              </i-form-item>
              <i-form-item
                :label="$t('common.fields.new_password_confirm')"
                :error="errors?.password_confirm?.[0]"
                :class="form.password_confirm ? 'has-value' : ''"
                prop="password_confirm"
                class="tw-flex-3"
              >
                <PasswordInput
                  v-model="form.password_confirm"
                  disable-autofill
                />
              </i-form-item>
            </div>
          </div>
        </div>
        <div
          class="tw-flex tw-flex-col"
          :class="[$isInRole(['FOREIGNER']) ? 'tw-flex-1' : 'tw-flex-2']"
        >
          <div class="tw-flex tw-flex-col tw-gap-1">
            <h3 class="tw-leading-6">
              {{ $t('main.me.notifications_title') }}
            </h3>
            <span
              v-if="$isInRole(['ADMIN', 'CONSULTANT', 'HR', 'AGENCY'])"
              class="tw-leading-5 tw-text-gray-dark tw-mb-4"
            >
              {{ $t('main.me.notifications_hint') }}
            </span>
          </div>
          <div class="tw-flex tw-flex-wrap tw-gap-x-10">
            <div
              v-for="(notificationSetting, index) in form.notification_settings_global"
              :key="index"
              class="tw-py-3"
              :class="[
                index !== 0 && index !== 1 ? `tw-border-t tw-border-t-gray` : '',
                [$isInRole(['FOREIGNER']) ? 'tw-w-full' : 'tw-w-[calc(50%-20px)] ']
              ]"
            >
              <el-switch
                v-model="notificationSetting.enabled"
                :disabled="!notificationSetting.can_be_override"
                :active-text="$capitalize(notificationSetting.notification_type)"
              />
            </div>
          </div>
        </div>
      </el-form>
      <div
        v-if="formHasChanged"
        class="tw-flex tw-py-4 tw-justify-end tw-gap-2 tw-border-t tw-border-t-gray"
      >
        <i-button

          class="tw-w-40"
          @click="resetForm"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          :disabled="!formHasChanged"
          @click="submitForm"
        >
          {{ $t('common.actions.edit') }}
        </i-button>
      </div>
    </div>
    <OutOfOfficeDialog
      v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
      :is-open="isOutOfOfficeDialogOpen"
      @close="isOutOfOfficeDialogOpen = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import _get from 'lodash/get';
import { deepDiff, getValidationError } from '@/helpers';
import { isObject, cloneDeep } from 'lodash';
import PasswordInput from '@/components/shared/PasswordInput.vue';
import OutOfOfficeDialog from '@/components/Dialogs/OutOfOfficeDialog.vue';

const FORM_BASE = {
  first_name: '', // required, maxLength: 30
  last_name: '', //  maxLength: 150
  phone_primary: '', // not required, but if entered maxLength: 32
  phone_secondary: '', // not required, but if entered maxLength: 32
  email: '',// required
  interface_language: '',// required
  password_current: '', // required
  password: '', // required
  password_confirm: '', // required
  notification_settings_global: [],
};

export default {
  name: 'ProfileSettingsV2',
  components: { OutOfOfficeDialog, PasswordInput },
  data() {
    return {
      isLoading: false,
      isDataLoaded: false,
      isAddDocumentsDialogOpen: false,
      isOutOfOfficeDialogOpen: false,
      formHasChanged: false,
      userAvatar: null,
      errors: {},
      form: cloneDeep(FORM_BASE),
      initialForm: {},
      formRules: {
        first_name: [
          { max: 30, message: 'Max 30 characters', trigger: 'blur' },
        ],
        last_name: [
          { max: 150, message: 'Max 150 characters', trigger: 'blur' },
        ],
        email: [
          { type: 'email', message: 'Please input correct email address', trigger: 'blur' },
        ],
        gender: [
          { required: false },
        ],
        role: [
          { required: false },
        ],
        phone_primary: [
          { required: false, message: 'Phone number length must be less than 32', trigger: 'blur', max: 32 },
        ],
        phone_secondary: [
          { required: false, message: 'Phone number length must be less than 32', trigger: 'blur', max: 32 },
        ],
        password_current: [
          { required: false },
        ],
        password: [
          {
            required: false, trigger: 'change', validator: (rule, value, callback) => {
              if (this.form.password_current.length === 0) return callback();

              const minLengthRegExp = new RegExp('.{8,}');
              const atLeastOneNumberAndCharRegExp = new RegExp(/^(?=.*[A-Za-z])(?=.*\d).+$/);
              if (value && !minLengthRegExp.test(value)) {
                callback(Error('Password must be at least 8 characters long'));
              } else if(value && !atLeastOneNumberAndCharRegExp.test(value)) {
                callback(Error('Password must contain at least one number and one letter'));
              } else {
                callback();
              }
            },
          },
        ],
        password_confirm: [
          {
            required: false, trigger: 'change', validator: (rule, value, callback) => {
              if (this.form.password_current.length === 0) return callback();
              if (value !== this.form.password) {
                callback(Error('Passwords do not match'));
              } else {
                callback();
              }
            },
          },
        ],
      },
    };
  },
  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      interfaceLanguages: (state) => state.v2.shared.enums.interface_languages,
    }),
    ...mapGetters({
      isOutOfOffice: 'v2/shared/profile/isOutOfOffice',
    }),
  },
  watch: {
    me() {
      this.setInitialFormData();
    },
    form: {
      handler() {
        this.formHasChanged = this.getIsFormChanged();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadProfileDetails();
  },
  methods: {
    getIsFormChanged() {
      return this.form.first_name !== this.initialForm.first_name
        || this.form.last_name !== this.initialForm.last_name
        || this.form.phone_primary !== this.initialForm.phone_primary
        || this.form.phone_secondary !== this.initialForm.phone_secondary
        || this.form.email !== this.initialForm.email
        || this.form.interface_language !== this.initialForm.interface_language
        || this.form.password_current.length !== 0
        || this.form.password.length !== 0
        || this.form.password_confirm.length !== 0
        || this.form.notification_settings_global?.some(
          (setting, index) =>
            setting.enabled !== this.initialForm.notification_settings_global[index].enabled,
        );
    },
    async uploadAvatar(payload) {
      try {
        const response = await this.$store.dispatch('v2/shared/profile/updateAvatar', { payload });

        this.$message.success({
          message: this.$capitalize(_get(response, 'data.message') || 'Success'),
          showClose: true,
        });
      } catch (error) {
        const errorText = getValidationError(error);
        this.$message.error({
          message: errorText,
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      }
    },
    async loadProfileDetails() {
      this.isDataLoaded = false;
      await this.$store.dispatch('v2/shared/profile/loadMe');
      this.isDataLoaded = true;
    },

    setInitialFormData() {
      const profileKeys = Object.keys(this.me);
      const formKeys = Object.keys(this.form);

      formKeys.forEach((key) => {
        if (profileKeys.includes(key)) {
          const value = this.me[key];
          if (isObject(value)) {
            this.form[key] = cloneDeep(value);
            this.initialForm[key] = cloneDeep(value);
          } else {
            this.form[key] = value;
            this.initialForm[key] = value;
          }
        }
      });

      this.initialForm.password_current = '';
      this.initialForm.password = '';
      this.initialForm.password_confirm = '';

      this.form.password_current = '';
      this.form.password = '';
      this.form.password_confirm = '';

      this.userAvatar = this.me.avatar_url;

      this.formHasChanged = false;

      this.$refs.Form?.clearValidate();
    },
    async submitForm() {
      this.errors = {};
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const payload = deepDiff(this.initialForm, this.form);
        const response = await this.$store.dispatch('v2/shared/profile/updateMe', { payload });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });
      } catch (error) {
        this.errors = error.response.data.data;

        this.$message.error({
          message: this.$capitalize(error.response.data.message),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
    resetForm() {
      this.form = cloneDeep(this.initialForm);
      this.form.password_current = '';
      this.form.password = '';
      this.form.password_confirm = '';
      this.formHasChanged = false;
      this.$refs.Form?.clearValidate();
    },
    async setIsAvailableNow() {
      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('v2/shared/profile/updateOutOfOffice', {
          startDate: null,
          startTime: null,
          endDate: null,
          endTime: null,
          replacementUserId: null,
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });
      } catch (error) {
        console.log('error', error);
        const errorText = getValidationError(error);

        this.$message.error({
          message: errorText,
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
